import { OrderRepository } from '../../domain/repositories/OrderRepository'
import { OrderMiniModel } from '../models/OrderMiniModel'
import type { OrderMiniEntity } from '../../domain/entities/OrderMiniEntity'
import {
  OrdersService,
  RemindersService,
  SearchDriversService,
  ShipmentsService,
  UserNotesService,
  ZohosService,
  type OrderAdminListedDtoAdmin,
  type PostOrderAdminNoteAdminRequest,
  type PutOrderItemsSigmaRequest
} from '@/backend/market'
import { PaginateEntity } from '@/shared/entities/PaginateEntity'
import type { OrderFilterEntity } from '../../domain/entities/OrderFilterEntity'
import { OrderFilterModel } from '../models/OrderFilterModel'
import { OrderModel } from '../models/OrderModel'
import type { OrderEntity } from '../../domain/entities/OrderEntity'
import { OrderItemModel } from '../models/OrderItemModel'
import type { OrderItemEntity } from '../../domain/entities/OrderItemEntity'
import { OrderStatusLogModel } from '../models/OrderStatusLogModel'
import type { OrderStatusLogEntity } from '../../domain/entities/OrderStatusLogEntity'
import type { CreateUpdateNoteEntity } from '@/features/note/domain/entities/CreateUpdateNoteEntity'
import { CreateUpdateNoteModel } from '@/features/note/data/models/CreateUpdateNoteModel'
import type { OrderAdminNoteEntity } from '../../domain/entities/OrderAdminNoteEntity'
import { OrderAdminNoteModel } from '../models/OrderAdminNoteModel'
import { AssignDriverModel } from '../models/AssignDriverModel'
import type { AssignDriverEntity } from '../../domain/entities/AssignDriverEntity'
import { OrderStatus } from '../../domain/enums/OrderStatus'
import type { OrderDiscountEntity } from '../../domain/entities/OrderDiscountEntity'
import { OrderDiscountModel } from '../models/OrderDiscountModel'
import { OrderItemsModel } from '../models/OrderItemsModel'
import type { OrderItemsEntity } from '../../domain/entities/OrderItemsEntity'
import { ItemsService } from '@/backend/market/services/ItemsService'
import type { OrderListedFilterEntity } from '../../domain/entities/OrderListedFilterEntity'
import { OrderListedFilterModel } from '../models/OrderListedFilterModel'
import { ReminderModel } from '../models/ReminderModel'
import type { ReminderEntity } from '../../domain/entities/ReminderEntity'
import { SearchDriverHistoryModel } from '../models/SearchDriverHistoryModel'
import type { SearchDriverHistoryEntity } from '../../domain/entities/SearchDriverHistoryEntity'
import type { UpdateOrderItemEntity } from '../../domain/entities/UpdateOrderItemEntity'
import { UpdateOrderItemModel } from '../models/UpdateOrderItemModel'
import type { CompensationFilterEntity } from '../../domain/entities/CompensationFilterEntity'
import { CompensationFilterModel } from '../models/CompensationFilterModel'
import { OrderCompensationModel } from '../models/OrderCompensationModel'
import type { OrderCompensationEntity } from '../../domain/entities/OrderCompensationEntity'
import type { CreateUpdateCompensationEntity } from '../../domain/entities/CreateUpdateCompensationEntity'
import { CreateUpdateCompensationModel } from '../models/CreateUpdateCompensationModel'
import type {
  AdminCancelOrderPayloadEntity,
  UnavailableItemDurationEntity
} from '../../domain/entities/AdminCancelOrderPayloadEntity'
import { buildAdminCancelOrderPayload } from '../../domain/utils/buildAdminCancelOrderPayload'
import type {
  DeductionPreviewEntity,
  OrderDeductionError,
  OrderDeductionPayload,
  OrderDeductionResult
} from '../../domain/entities/OrderDeductionEntity'
import { getOrderDeductionError } from '../../domain/utils/deductionErrors'

export class OrderRepositoryImpl implements OrderRepository {
  async getOrders(
    filterData: OrderFilterEntity
  ): Promise<PaginateEntity<OrderMiniEntity[]> | { error: true }> {
    const orderFilterModel = new OrderFilterModel(filterData)
    try {
      const response = await OrdersService.getOrdersAdmin(orderFilterModel as {})

      const rawItems = response.data || []

      const orderModels = rawItems.map((data: OrderAdminListedDtoAdmin) => new OrderMiniModel(data))

      const metadata = {
        nextCursor: response.nextCursor,
        hasNextPage: response.hasNextPage
      }

      const paginateData = {
        items: orderModels, // PaginateEntity expects mapped items usually or raw?
        // PaginateEntity constructor signature: constructor(data: Partial<Record<keyof PaginateEntity<T>, unknown>>, model?: T)
        // We can construct it directly with our data.
        // Wait, PaginateEntity constructor maps "items" from "model" arg if present, or "data" arg if present.
        // Let's rely on constructing it cleanly.
        metadata: metadata
      }

      // We need to match the signature of PaginateEntity.
      // It seems it takes (data, model).
      // 'data' is the raw response usually containing count/page etc.
      // 'model' is the mapped items array.

      const orderResponse = new PaginateEntity<OrderMiniModel[]>(
        {
          ...paginateData
        },
        orderModels
      )

      return orderResponse ?? []
    } catch {
      return { error: true }
    }
  }
  async getOrder(id: string): Promise<OrderEntity | { error: true }> {
    try {
      const response = await OrdersService.getOrderAdmin({ id })
      const orderModel = new OrderModel(response)

      return orderModel
    } catch {
      return { error: true }
    }
  }
  async getUserNote(userId: string): Promise<string | { error: true }> {
    try {
      const response = await UserNotesService.getLatestUserNoteAdmin({ userId })
      return response.note ?? ''
    } catch {
      return { error: true }
    }
  }
  async getOrderDiscount(id: string): Promise<OrderDiscountEntity | { error: true }> {
    try {
      const response = await OrdersService.getOrderDiscountAdmin({ id })
      const orderModel = new OrderDiscountModel(response.discount ?? {})

      return orderModel
    } catch {
      return { error: true }
    }
  }
  async getOrderStatusLog(id: string): Promise<OrderStatusLogEntity[] | { error: true }> {
    try {
      const response = await OrdersService.getOrderStatusLogsAdmin({ id })
      const orderItemModels = response.items?.map(
        (item) => new OrderStatusLogModel(item as OrderStatusLogModel)
      )
      return orderItemModels ?? []
    } catch {
      return { error: true }
    }
  }

  async getOrderItems(
    id: string
  ): Promise<
    { items?: OrderItemEntity[]; branchNote?: string; driverNote?: string } | { error: true }
  > {
    try {
      const response = await OrdersService.getOrderItemsAdmin({ id })
      const orderItemModels = response.orderItems?.map((item) => new OrderItemModel(item))

      return {
        items: orderItemModels || [],
        branchNote: response.branchNote ?? '',
        driverNote: response.driverNote ?? ''
      }
    } catch {
      return { error: true }
    }
  }
  async getOrderItemsList(
    filterData: OrderListedFilterEntity
  ): Promise<PaginateEntity<OrderItemsEntity[]> | { error: true }> {
    try {
      const filterModel = new OrderListedFilterModel(filterData)
      const response = await ItemsService.getDiscountedItemsAdmin(filterModel as {})
      const orderItemModels = response.items?.map((item) => new OrderItemsModel(item))
      const orderItemsResponse = new PaginateEntity<OrderItemsModel[]>(response, orderItemModels)

      return orderItemsResponse
    } catch {
      return { error: true }
    }
  }
  async updateOrderItems(
    id: string,
    data: UpdateOrderItemEntity
  ): Promise<{ success: true } | { error: true }> {
    const updateOrderItemModel = new UpdateOrderItemModel(data)
    try {
      await OrdersService.putOrderItemsAdmin({
        id,
        requestBody: updateOrderItemModel as unknown as PutOrderItemsSigmaRequest
      })

      return { success: true }
    } catch {
      return { error: true }
    }
  }

  async acceptOrderByAdmin(id: string): Promise<{ success: true } | { error: true }> {
    try {
      await OrdersService.postOrderApproveAdmin({
        id
      })

      return { success: true }
    } catch {
      return { error: true }
    }
  }

  async acceptOrderByOperator(
    id: string,
    estimatedPreparationTimeInMinute: number
  ): Promise<{ success: true } | { error: true }> {
    try {
      await OrdersService.postOrderAcceptAdmin({
        id,
        requestBody: {
          estimatedPreparationTimeInMinute
        }
      })

      return { success: true }
    } catch {
      return { error: true }
    }
  }

  async cancelOrderByAdmin(
    id: string,
    data: AdminCancelOrderPayloadEntity
  ): Promise<{ isSuccess: true } | { error: true; message?: string }> {
    try {
      const cancelResult = await this.submitAdminCancelOrder(id, data)
      if (cancelResult.error) {
        return cancelResult
      }
      return { isSuccess: true }
    } catch (error) {
      return { error: true, message: this.extractErrorMessage(error) }
    }
  }

  async watchOrder(id: string): Promise<{ success: true } | { error: true }> {
    try {
      await OrdersService.postOrderTrackingAdmin({
        id
      })

      return { success: true }
    } catch {
      return { error: true }
    }
  }
  async createOrderNote(
    orderId: string,
    noteData: CreateUpdateNoteEntity
  ): Promise<{ success: true } | { error: true }> {
    const createUpdateNoteModel = new CreateUpdateNoteModel(noteData)
    try {
      await OrdersService.postOrderAdminNoteAdmin({
        orderAdminNoteId: orderId,
        requestBody: createUpdateNoteModel as unknown as PostOrderAdminNoteAdminRequest
      })

      return { success: true }
    } catch {
      return { error: true }
    }
  }
  async updateOrderNote(
    orderAdminNoteId: string,
    noteData: CreateUpdateNoteEntity
  ): Promise<{ success: true } | { error: true }> {
    const createUpdateNoteModel = new CreateUpdateNoteModel(noteData)
    try {
      await OrdersService.putOrderAdminNoteAdmin({
        orderAdminNoteId,
        requestBody: createUpdateNoteModel
      })

      return { success: true }
    } catch {
      return { error: true }
    }
  }
  async deleteOrderNote(orderAdminNoteId: string): Promise<{ success: true } | { error: true }> {
    try {
      await OrdersService.deleteOrderAdminNoteAdmin({
        orderAdminNoteId
      })

      return { success: true }
    } catch {
      return { error: true }
    }
  }
  async getOrderNote(id: string): Promise<OrderAdminNoteEntity[] | { error: true }> {
    try {
      const response = await OrdersService.getOrderAdminNoteAdmin({
        id
      })
      const orderAdminNoteModel = response.orderAdminNotes?.map(
        (item) => new OrderAdminNoteModel(item)
      )

      return orderAdminNoteModel ?? []
    } catch {
      return { error: true }
    }
  }
  async assignDeliveryDriver(
    id: string,
    assignData: AssignDriverEntity
  ): Promise<{ success: true } | { error: true }> {
    const assignDriverModel = new AssignDriverModel(assignData)
    try {
      await OrdersService.postOrderAssignAdmin({
        id,
        requestBody: assignDriverModel
      })
      return { success: true }
    } catch {
      return { error: true }
    }
  }
  async unAssignDeliveryDriver(id: string): Promise<{ success: true } | { error: true }> {
    try {
      await OrdersService.postOrderUnassignAdmin({
        id
      })
      return { success: true }
    } catch {
      return { error: true }
    }
  }
  async acquireDeliveryDriver(
    id: string,
    assignData: AssignDriverEntity
  ): Promise<{ success: true } | { error: true }> {
    const assignDriverModel = new AssignDriverModel(assignData)
    try {
      await OrdersService.postOrderAcquireAdmin({
        id,
        requestBody: assignDriverModel
      })
      return { success: true }
    } catch {
      return { error: true }
    }
  }
  async broadcastDelivery(id: string): Promise<{ success: true } | { error: true }> {
    try {
      await OrdersService.postBroadcastDeliveryAdmin({
        id,
        requestBody: {
          excludeDriverIds: []
        }
      })
      return { success: true }
    } catch {
      return { error: true }
    }
  }
  async updateDeduction(
    id: string,
    deductionData: OrderDeductionPayload
  ): Promise<OrderDeductionResult> {
    try {
      await OrdersService.postOrderDeductionAdmin({
        id,
        requestBody: deductionData
      })
      return { success: true }
    } catch (error) {
      return getOrderDeductionError(error)
    }
  }

  async previewDeduction(
    id: string,
    deductionData: OrderDeductionPayload
  ): Promise<DeductionPreviewEntity | OrderDeductionError> {
    try {
      return await OrdersService.postOrderDeductionPreviewAdmin({
        id,
        requestBody: deductionData
      })
    } catch (error) {
      return getOrderDeductionError(error)
    }
  }
  async changeOrderLocation(
    id: string,
    location: {
      latitude: number
      longitude: number
    }
  ): Promise<{ success: true } | { error: true }> {
    try {
      await OrdersService.putOrderAddressAdmin({
        id,
        requestBody: {
          location
        }
      })
      return { success: true }
    } catch {
      return { error: true }
    }
  }

  async getStatusUpdateOptions(orderStatus: OrderStatus): Promise<OrderStatus[] | { error: true }> {
    try {
      const response = await OrdersService.getOrderStatusCanBeUpdatedToAdmin({
        orderStatus
      })
      return response.orderStatuses ?? []
    } catch {
      return { error: true }
    }
  }
  async updateOrderStatus({
    id,
    orderStatus,
    estimatedPreparationTimeInMinute,
    cancelOrderReasonId,
    cancelShipmentReasonId,
    shipmentId,
    unavailableItems,
    requiresUnavailableItems
  }: {
    id: string
    orderStatus: OrderStatus
    estimatedPreparationTimeInMinute?: number
    cancelOrderReasonId?: number
    cancelShipmentReasonId?: number
    unavailableItems?: UnavailableItemDurationEntity[]
    requiresUnavailableItems?: boolean
    shipmentId?: string
  }): Promise<{ success: true } | { error: true; message?: string }> {
    try {
      if (cancelShipmentReasonId) {
        await ShipmentsService.postCancelShipmentAdmin({
          id: shipmentId!,
          requestBody: {
            cancelShipmentReasonId: cancelShipmentReasonId ?? 0
          }
        })
        return { success: true }
      } else {
        switch (orderStatus) {
          case OrderStatus.APPROVED:
            await OrdersService.postOrderApproveAdmin({
              id
            })
            return { success: true }

          case OrderStatus.ACCEPTED:
            await OrdersService.postOrderAcceptAdmin({
              id,
              requestBody: {
                estimatedPreparationTimeInMinute: estimatedPreparationTimeInMinute ?? 0
              }
            })
            return { success: true }
          case OrderStatus.DRIVER_REACHED_BRANCH:
            await OrdersService.postDriverReachedBranchAdmin({
              id: id
            })
            return { success: true }
          case OrderStatus.PICKED_UP:
            await OrdersService.postOrderPickupAdmin({
              id
            })
            return { success: true }
          case OrderStatus.AT_DESTINATION:
            await OrdersService.postReachDestinationAdmin({
              id: id
            })
            return { success: true }
          case OrderStatus.COMPLETED:
            await OrdersService.postOrderCompleteAdmin({
              id
            })
            return { success: true }
          case OrderStatus.NOT_DELIVERED:
            await OrdersService.postOrderNotDeliveredAdmin({
              id
            })
            return { success: true }
          case OrderStatus.CANCELED: {
            const cancelResult = await this.submitAdminCancelOrder(id, {
              cancelOrderReasonId,
              unavailableItems,
              requiresUnavailableItems
            })
            if (cancelResult.error) {
              return cancelResult
            }
            return { success: true }
          }
          case OrderStatus.DELIVERING:
            await OrdersService.postOrderDeliveringAdmin({
              id
            })
            return { success: true }
          default:
            break
        }
      }
      return { error: true }
    } catch (error) {
      return { error: true, message: this.extractErrorMessage(error) }
    }
  }

  async createZohoInvoiceOrder(id: string): Promise<{ success: true } | { error: true }> {
    try {
      await OrdersService.postInvoiceAdmin({
        orderId: id
      })
      return { success: true }
    } catch {
      return { error: true }
    }
  }

  async deleteZohoInvoiceOrder(id: string): Promise<{ success: true } | { error: true }> {
    try {
      await OrdersService.deleteInvoiceAdmin({
        orderId: id
      })
      return { success: true }
    } catch {
      return { error: true }
    }
  }

  async createZohoPaymentOrder(id: string): Promise<{ success: true } | { error: true }> {
    try {
      await ZohosService.postInvoiceFulfillmentPaymentAdmin({
        id
      })
      return { success: true }
    } catch {
      return { error: true }
    }
  }

  async deleteZohoPaymentOrder(id: string): Promise<{ success: true } | { error: true }> {
    try {
      await OrdersService.deletePaymentAdmin({
        orderId: id
      })
      return { success: true }
    } catch {
      return { error: true }
    }
  }

  async createZohoCreditOrder(id: string): Promise<{ success: true } | { error: true }> {
    try {
      await OrdersService.postCreditApplyAdmin({
        orderId: id
      })
      return { success: true }
    } catch {
      return { error: true }
    }
  }
  async getOrderReminders(id: string): Promise<ReminderEntity[] | { error: true }> {
    try {
      const response = await RemindersService.getReminderAttemptsByOrderIdAdmin({
        orderId: id
      })
      const reminderModels = response.reminderAttempts?.map(
        (item) => new ReminderModel(item as ReminderModel)
      )
      return reminderModels ?? []
    } catch {
      return { error: true }
    }
  }
  async getOrderSearchDriversHistory(
    id: string
  ): Promise<SearchDriverHistoryEntity[] | { error: true }> {
    try {
      const response = await SearchDriversService.getSearchDriversByOrderIdAdmin({
        orderId: id
      })
      const searchDriverHistoryModels = response.searchDrivers?.map(
        (item) => new SearchDriverHistoryModel(item)
      )
      return searchDriverHistoryModels ?? []
    } catch {
      return { error: true }
    }
  }
  async getOrderCompensations(
    filterData: CompensationFilterEntity
  ): Promise<OrderCompensationEntity[] | { error: true }> {
    const filterModel = new CompensationFilterModel(filterData)
    try {
      const response = await OrdersService.getOrderCompensationsAdmin(filterModel)
      const orderCompensationModels = response.items?.map(
        (item) => new OrderCompensationModel(item)
      )
      return orderCompensationModels ?? []
    } catch {
      return { error: true }
    }
  }
  async createOrderCompensation(
    id: string,
    data: CreateUpdateCompensationEntity
  ): Promise<{ success: true } | { error: true }> {
    const createUpdateCompensationModel = new CreateUpdateCompensationModel(data)
    try {
      await OrdersService.postOrderCompensationAdmin({
        id,
        requestBody: createUpdateCompensationModel
      })
      return { success: true }
    } catch {
      return { error: true }
    }
  }

  private async submitAdminCancelOrder(
    id: string,
    data: AdminCancelOrderPayloadEntity
  ): Promise<{ success: true } | { error: true; message?: string }> {
    const payloadResult = buildAdminCancelOrderPayload(data)
    if (!payloadResult.payload) {
      return { error: true, message: payloadResult.errorMessage }
    }

    try {
      await OrdersService.postCancelOrderAdmin({
        id,
        requestBody: payloadResult.payload
      })
      return { success: true }
    } catch (error) {
      return { error: true, message: this.extractErrorMessage(error) }
    }
  }

  private extractErrorMessage(error: unknown) {
    const fallbackMessage = 'Failed to cancel order. Please review your inputs and try again.'

    if (!error || typeof error !== 'object') {
      return fallbackMessage
    }

    const apiError = error as {
      body?: {
        message?: string
        detail?: string
        errors?: Array<{ message?: string }>
      }
      message?: string
    }

    if (apiError.body?.message) return apiError.body.message
    if (apiError.body?.detail) return apiError.body.detail
    if (apiError.body?.errors?.length) {
      return apiError.body.errors
        .map((item) => item.message)
        .filter(Boolean)
        .join(', ')
    }
    return apiError.message ?? fallbackMessage
  }
}
